import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-practices',
  templateUrl: './practices.component.html',
  styleUrls: ['./practices.component.scss']
})
export class PracticesComponent implements OnInit {
  AppConstants = AppConstants;
  practices: any[] = [];
  tableColumns: TableColumn[] = [];
  showConfirmDialog: boolean = false;
  pendingAction: 'Submit' | 'Approve' | 'Publish' | null = null;
  pendingPracticeId: string | null = null;

  constructor(
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private _router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeTableColumns();
    this.fetchPractices();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        iconPath: 'assets/icons/edit-icon.svg',
        action: 'edit',
        title: 'Edit'
      },
      {
        icon: `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.99935 18.3332C14.6017 18.3332 18.3327 14.6022 18.3327 9.99984C18.3327 5.39746 14.6017 1.6665 9.99935 1.6665C5.39698 1.6665 1.66602 5.39746 1.66602 9.99984C1.66602 14.6022 5.39698 18.3332 9.99935 18.3332Z" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        action: 'approve',
        title: 'Approve',
        condition: (row: any) => this.shouldShowAction(row, 'approve')
      },
      {
        icon: `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.99935 18.3332C14.6017 18.3332 18.3327 14.6022 18.3327 9.99984C18.3327 5.39746 14.6017 1.6665 9.99935 1.6665C5.39698 1.6665 1.66602 5.39746 1.66602 9.99984C1.66602 14.6022 5.39698 18.3332 9.99935 18.3332Z" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335" stroke="#4A5565" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        action: 'publish',
        title: 'Publish',
        condition: (row: any) => this.shouldShowAction(row, 'publish')
      }
    ];

    this.tableColumns = [
      {
        field: 'practiceId',
        header: 'Practice ID',
        type: 'text'
      },
      {
        field: 'title',
        header: 'Title',
        type: 'text'
      },
      {
        field: 'linkedPR',
        header: 'Linked PR',
        type: 'link'
      },
      {
        field: 'type',
        header: 'Type',
        type: 'badge',
        badgeClass: 'type-badge'
      },
      {
        field: 'submissionDeadline',
        header: 'Submission Deadline',
        type: 'date',
        format: 'dd-MM-yyyy'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'workflowStatus',
        header: 'Workflow Status',
        type: 'badge',
        badgeClass: 'workflow-status-badge'
      },
      {
        field: 'vendorsInvited',
        header: 'Vendors Invited',
        type: 'number'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: actionIcons
      }
    ];
  }

  async fetchPractices(): Promise<void> {
    this._loaderService.showLoader();
    
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.TENDERS}/${AppConstants.ACTIONS.GetTenderListData}?scope=0`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url)
      );

      // Check if request was successful
      if (!response?.isSuccess && !response?.IsSuccess) {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || response?.errorMessageAr || response?.ErrorMessageAr || 'Failed to fetch practices';
        console.error(`[Practices] API Error: ${errorMsg}`);
        this.practices = [];
        this.cdr.detectChanges();
        return;
      }

      // Extract practices data from response
      const practicesData = response?.data || [];
      
      if (Array.isArray(practicesData) && practicesData.length > 0) {
        this.practices = practicesData.map((practice: any) => this.mapToPracticeData(practice));
      } else {
        this.practices = [];
      }
      
      this.cdr.detectChanges();
      
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Practices] Exception while fetching practices:`, errorMessage, error);
      this.practices = [];
      this.cdr.detectChanges();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  /**
   * Maps raw API response data to the practice data structure expected by the component
   */
  private mapToPracticeData(practice: any): any {
    return {
      practiceId: practice.tenderID || practice.tenderId || '',
      title: practice.tenderName || practice.description || practice.TenderName || '',
      linkedPR: practice.linkedPR || practice.purchaseRequisition || practice.PurchaseRequisition || '',
      type: practice.typeofTender || practice.tenderType || '',
      submissionDeadline: this.parseDate(practice.acceptanceDeadline),
      status: practice.status || '',
      workflowStatus: this.formatWorkflowStatus(practice.workflowStatus || practice.WorkflowStatus || practice.workFlowStatus || practice.WorkFlowStatus || ''),
      vendorsInvited: practice.totalInvitedMembers || practice.TotalInvitedMembers || 0
    };
  }

  formatWorkflowStatus(status: any): string {
    if (!status) return 'Not Submitted';
    
    // Handle enum values (numbers)
    if (typeof status === 'number') {
      switch(status) {
        case 0: return 'Not Submitted';
        case 1: return 'Pending Publication';
        case 2: return 'Approved';
        case 3: return 'Rejected';
        case 4: return 'Published';
        default: return 'Unknown';
      }
    }
    
    // Handle string values
    const statusStr = String(status).trim();
    if (!statusStr) return 'Not Submitted';
    
    // Convert common formats to display format
    switch(statusStr.toLowerCase()) {
      case 'notsubmitted':
      case 'not_submitted':
        return 'Not Submitted';
      case 'pendingapproval':
      case 'pending_approval':
        return 'Pending Publication';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'published':
        return 'Published';
      default:
        // Return formatted version (e.g., "PendingApproval" -> "Pending Approval")
        return statusStr.replace(/([A-Z])/g, ' $1').trim();
    }
  }

  /**
   * Parses a date value into a Date object
   * Returns null if the date value is null or invalid
   */
  private parseDate(dateValue: any): Date | null {
    if (!dateValue || dateValue === null) {
      return null;
    }
    
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    try {
      const parsedDate = new Date(dateValue);
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
      return parsedDate;
    } catch {
      return null;
    }
  }

  createPractice(): void {
    this._router.navigate([AppConstants.ROUTES.PRACTICE, AppConstants.ROUTES.CREATE_PRACTICE]);
  }

  uploadPractices(): void {
    // TODO: Implement upload functionality
    console.log('Upload Practices');
  }

  onTableActionClick(event: {action: string, row: any}): void {
    switch(event.action) {
      case 'view':
        this.viewPractice(event.row.practiceId);
        break;
      case 'edit':
        this.editPractice(event.row.practiceId);
        break;
      case 'document':
        this.viewDocuments(event.row.practiceId);
        break;
      case 'submit':
        this.submitPractice(event.row.practiceId);
        break;
      case 'approve':
        this.approvePractice(event.row.practiceId);
        break;
      case 'publish':
        this.publishPractice(event.row.practiceId);
        break;
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'practiceId') {
      this.viewPractice(event.row.practiceId);
    } else if (event.field === 'linkedPR') {
      // TODO: Navigate to PR details
      console.log('Navigate to PR:', event.row.linkedPR);
    }
  }

  viewPractice(practiceId: string): void {
    this._router.navigate([`/${AppConstants.ROUTES.PRACTICE}/${AppConstants.ROUTES.PRACTICEDETAILS}/${practiceId}`]);
  }

  editPractice(practiceId: string): void {
    // Navigate to practice details page with edit mode
    this._router.navigate([`/${AppConstants.ROUTES.PRACTICE}/${AppConstants.ROUTES.PRACTICEDETAILS}/${practiceId}`], {
      queryParams: { edit: 'true' }
    });
  }

  viewDocuments(practiceId: string): void {
    // TODO: Open documents dialog
    console.log('View Documents:', practiceId);
  }

  shouldShowAction(row: any, action: 'submit' | 'approve' | 'publish'): boolean {
    const status = (row.status || '').toLowerCase();
    
    // Submit: Show for draft or initial status
    if (action === 'submit') {
      return status === 'draft' || status === '' || status === 'in progress' || status === 'pending';
    }
    
    // Approve: Show after submit (when status is submitted or under evaluation)
    if (action === 'approve') {
      return status === 'submitted' || status === 'under evaluation' || status === 'evaluation';
    }
    
    // Publish: Show when ready to publish (after approval)
    if (action === 'publish') {
      return status === 'approved' || status === 'confirmed' || status === 'ready to publish';
    }
    
    return false;
  }

  submitPractice(practiceId: string): void {
    this.pendingAction = 'Submit';
    this.pendingPracticeId = practiceId;
    this.showConfirmDialog = true;
  }

  approvePractice(practiceId: string): void {
    this.pendingAction = 'Approve';
    this.pendingPracticeId = practiceId;
    this.showConfirmDialog = true;
  }

  publishPractice(practiceId: string): void {
    this.pendingAction = 'Publish';
    this.pendingPracticeId = practiceId;
    this.showConfirmDialog = true;
  }

  confirmWorkflowAction(): void {
    if (!this.pendingAction || !this.pendingPracticeId) return;
    
    switch (this.pendingAction) {
      case 'Submit':
        this.executeSubmitPractice(this.pendingPracticeId);
        break;
      case 'Approve':
        this.executeApprovePractice(this.pendingPracticeId);
        break;
      case 'Publish':
        this.executePublishPractice(this.pendingPracticeId);
        break;
    }
    
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.pendingPracticeId = null;
  }

  cancelWorkflowAction(): void {
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.pendingPracticeId = null;
  }

  getConfirmDialogMessage(): string {
    if (!this.pendingAction) return '';
    return `Are you sure you want to ${this.pendingAction} this practice?`;
  }

  private executeSubmitPractice(practiceId: string): void {
    // TODO: Implement submit practice functionality
    console.log('Submit Practice:', practiceId);
    // After successful submit, refresh the practices list
    // this.fetchPractices();
  }

  private executeApprovePractice(practiceId: string): void {
    // TODO: Implement approve practice functionality
    console.log('Approve Practice:', practiceId);
    // After successful approve, refresh the practices list
    // this.fetchPractices();
  }

  private executePublishPractice(practiceId: string): void {
    // TODO: Implement publish practice functionality
    console.log('Publish Practice:', practiceId);
    // After successful publish, refresh the practices list
    // this.fetchPractices();
  }
}
